import * as React from 'react';
import { Collapse, Row } from 'reactstrap'
import classNames from 'classnames';

import { accentUtils } from '../../services/HelperService';
import { configFrom, optionDisplayTypes } from '../../services/ProductService';

import { ConfigCheckbox } from './ConfigCheckbox';
import { ConfigDecimal } from './ConfigDecimal';
import { ConfigExtras } from './ConfigExtras';
import { ConfigLength } from './ConfigLength';
import { ConfigNumeric } from './ConfigNumeric';
import { ConfigPanelPattern } from './ConfigPanelPattern';
import { ConfigPicklist } from './ConfigPicklist';
import { ConfigSingleLineFabric } from './ConfigSingleLineFabric';
import { ConfigSizeAllowed } from './ConfigSizeAllowed';
import { ConfigSizeSingle } from './ConfigSizeSingle';
import { ConfigText } from './ConfigText';
import { ConfigTwoLineFabric } from './ConfigTwoLineFabric';
import { ConfiguratorContext } from './ConfigContextProvider';

import './ProductConfigurator.css';
import { ActionBar, ConfigLabel, getSizeRequirements, isOptionVisible, isReadOnly, SizeIndicator } from './ProductConfiguratorShared';

import { ConfigSizeAction } from './SelectSizeDlg';
import AccentIcons from '../../icons/Icons'

export const VerticalConfiguratorCtrl = React.memo(props => {

    const ctx = React.useContext(ConfiguratorContext)

    const groups = configFrom(ctx.config.GetActiveGroupsForOrder())
        .where(g => !g.MultiProductOption)
        .select(g => <Group className="options" key={`key_${g.ID}`} group={g} />).toArray();

    const extras = props.hideExtras ? null : configFrom(ctx.config.GetActiveGroupsForOrder())
        .where(g => g.MultiProductOption)
        .select(g => <Group className="extras" group={g} key={`key_${g.ID}`} />).toArray();

    React.useEffect(() => {
        ctx.config.ValidateOptions(true);
    }, []);

    return (
        <div className="config-vertical">
            {!props.hideSize && <div className="config-group-container">
                <SizeGroup />
            </div>}
            <div className="config-group-container">
                {groups}
            </div>
            {!props.hideExtras && < div className="config-extras-container">
                {extras}
            </div>}
        </div>
    );
});

const SizeGroup = props => {
    const ctx = React.useContext(ConfiguratorContext);

    const productSize = ctx.config.Data.GetSize();
    const sizeReqs = productSize ? getSizeRequirements(productSize) : null;
    const isHidden = sizeReqs && (sizeReqs.requireWidth || sizeReqs.requireHeight);

    const [expanded, setExpanded] = React.useState(true);

    const expandedUpIcon = <AccentIcons.Up onClick={() => setExpanded(false)} />;
    const expandedDownIcon = <AccentIcons.Down onClick={() => setExpanded(true)} />;

    const expandedIcon = expanded ? expandedUpIcon : expandedDownIcon;

    return (
        <div className="config-group size" hidden={!isHidden}>
            <div className="config-group-header d-flex">
                <div className="p-2" style={{ cursor: "pointer" }} onClick={() => setExpanded(!expanded)}>
                    Size
                </div>
                <div>{expandedIcon}</div>
            </div>
            <Collapse isOpen={expanded}>
                <Size productSize={productSize} sizeReqs={sizeReqs} />
            </Collapse>
        </div>
    );
};

const Size = ({ productSize, sizeReqs }) => {
    if (!productSize || !sizeReqs) return null;

    const width = sizeReqs.requireWidth
        ? <SizeWidth productSize={productSize} requireMulti={sizeReqs.requireMultiWidth || sizeReqs.requireMultiHeight} />
        : null;

    const height = sizeReqs.requireHeight
        ? <SizeHight productSize={productSize} requireMulti={sizeReqs.requireMultiWidth || sizeReqs.requireMultiHeight} />
        : null;

    return (
        <div className="config-size-container">
            {width}
            {height}
        </div>
    );
};

const SizeWidth = props => {
    return (
        <div className="config-size">
            <div className="config-size-header">
                <SizeIndicator width />
                <div className="config-size-label">Width</div>
            </div>
            <SizeCtrl width allowedValuesField="AllowedWidthList" productSize={props.productSize} requireMulti={props.requireMulti} />
        </div>
    );
};

const SizeHight = props => {
    return (
        <div className="config-size">
            <div className="config-size-header">
                <SizeIndicator />
                <div className="config-size-label">Height</div>
            </div>
            <SizeCtrl allowedValuesField="AllowedHeightList" productSize={props.productSize} requireMulti={props.requireMulti} />
        </div>
    );
};

export const SizeCtrl = React.memo(props => {

    const fixedValues = React.useMemo(() => accentUtils.isEmpty(props.productSize[props.allowedValuesField]) ? [] : JSON.parse(props.productSize[props.allowedValuesField]), [props.productSize]);
    const isFixed = fixedValues.length !== 0;

    const className = classNames("config-size-value-container", { "small": props.small, "multi": props.requireMulti });

    return (
        <div className={className}>
            {!isFixed && <ConfigSizeSingle width={props.width} readOnly={props.requireMulti} isMulti={props.requireMulti} />}
            {isFixed && <ConfigSizeAllowed width={props.width} readOnly={props.requireMulti} sizes={fixedValues} small={props.small} />}
            <div className="config-size-value-actions">
                {props.requireMulti && <ConfigSizeAction width={props.width} />}
            </div>
        </div>
    );
});

const Group = props => {

    const ctx = React.useContext(ConfiguratorContext)
    const group = props.group;

    // Get the options included in the Group
    const initialOptions = React.useMemo(() => {
        return group.MultiProductOption
            ? []
            : configFrom(ctx.config.GetActiveProductOptionsForOrder(group.ID)).toArray();
    }, [ctx.config, group.ID, group.MultiProductOption]);

    // Initialise a visibility map based on the starting visibility of each child (Option)
    const initialVisibilityMap = React.useMemo(() => {
        const map = {};
        for (const option of initialOptions) {
            const model = ctx.config.GetOptionModel(option.ID);
            map[option.ID] = isOptionVisible(model, ctx);
        }
        return map;
    }, [ctx, initialOptions])

    // Create a visibility map that will dynamically update
    const [visibilityMap, setVisibilityMap] = React.useState(initialVisibilityMap);

    // Callback for child Options to report their current visibility
    const visibility = React.useCallback((id, visible) => {
        setVisibilityMap(prev => {
            if (prev[id] === visible) return prev;
            return { ...prev, [id]: visible };
        });
    }, []);

    // Create the Options element(s) for display
    const options = initialOptions.map(o => 
        <Option key={`key_${o.ID}`} option={o} visibility={visibility}/>
    );

    // Check if the Group should be hidden
    const isHidden = group.MultiProductOption
        ? false
        : ((initialOptions.length > 0 && initialOptions.every(o => visibilityMap[o.ID] === false))
            || initialOptions.length === 0
        );

    // Create variables and logic for expanding and collapsing the Group Header
    const [expanded, setExpanded] = React.useState(true);

    const expandedUpIcon = <AccentIcons.Up onClick={() => setExpanded(false)} />;
    const expandedDownIcon = <AccentIcons.Down onClick={() => setExpanded(true)} />;

    var expandedIcon = expanded ? expandedUpIcon : expandedDownIcon;

    return (
        <div className={`config-group ${props.className}`} hidden={isHidden}>
            <div className="config-group-header d-flex">
                <div className="p-2" style={{ cursor: "pointer" }} onClick={() => setExpanded(!expanded)}>
                    {group.Description}
                </div>
                <div>{expandedIcon}</div>
            </div>
            <Collapse isOpen={expanded}>
                <div className="config-option-container">
                    <Row>
                        {group.MultiProductOption
                            ? <ConfigExtras groupID={group.ID.toString()} />
                            : options}
                    </Row>
                </div>
            </Collapse>
        </div>
    );
}

const Option = props => {

    const ctx = React.useContext(ConfiguratorContext)

    const option = props.option;
    const optionModel = ctx.config.GetOptionModel(option.ID);

    const isVisible = isOptionVisible(optionModel, ctx.filter);
    const readOnly = isReadOnly(optionModel);

    // To be able to hide the Group if all options are not visible, we need to report the visibility back to the parent Group
    React.useEffect(() => {
        props.visibility?.(option.ID, isVisible);
    }, [isVisible, option.ID, props.visibility]);

    const className = classNames("config-option", {
        "option-is-filtered": !isVisible,
        "readonly" : readOnly
    })

    return (
        <div className={className}>
            <div className="config-option-header"><ConfigLabel option={props.option}/></div>
            <div className="config-option-value-container">
                <OptionValue optionID={option?.ID?.toString()} displayType={optionModel.DisplayType} customDisplayType={option.CustomDisplayType}/>            
                <ActionBar optionID={option?.ID?.toString()}/>
            </div>
        </div>
    );
};

export const OptionValue = props => {

    let ctrl = null;
    
    switch (props.displayType) {
        case optionDisplayTypes.Picklist:
            ctrl = <ConfigPicklist optionID={props.optionID} small={ props.small } />; break;
        case optionDisplayTypes.Numeric: ctrl = ctrl = <ConfigNumeric optionID={props.optionID} />; break;
        case optionDisplayTypes.Decimal: ctrl = ctrl = <ConfigDecimal optionID={props.optionID} />; break;
        case optionDisplayTypes.Checkbox: ctrl = ctrl = <ConfigCheckbox optionID={props.optionID} />; break;
        case optionDisplayTypes.Length: ctrl = ctrl = <ConfigLength optionID={props.optionID} />; break;
        case "Custom":

            if (props.customDisplayType === "SoftFurnishings.Controls.FabricOption2LineCtrl, SoftFurnishings") {
                ctrl = ctrl = <ConfigTwoLineFabric optionID={props.optionID} small={props.small} />; break;                
            } else if (props.customDisplayType === "SoftFurnishings.Controls.FabricOptionCtrl, SoftFurnishings") {
                ctrl = ctrl = <ConfigSingleLineFabric optionID={props.optionID} />; break;
            } else if (props.customDisplayType === "InsyteProductModule.Controls.PanelPatternOptionCtrl, InsyteProductModule") {
                ctrl = ctrl = <ConfigPanelPattern optionID={props.optionID} />; break;
            }

            break;
        default:
            ctrl = <ConfigText optionID={props.optionID} />;
            break;
    }

    const className = classNames("config-value", { "small": props.small });

    return (
        <div className={className} data-tagtname={props.optionID}>
            {ctrl}
        </div>
    );
};